import { useEffect, useState } from 'react';
import { api, formatTl } from '../lib/api';
import AraSec from '../ui/AraSec';

type Cari = { id: number; name: string; phone: string | null };
type Urun = { id: number; code: string; name: string; offrec: number | null; listPriceKurus: number | null };
type Satir = { key: number; productId?: number; code: string; name: string; qty: string; unitTl: string; mevcut?: number };

let sayac = 0;

/**
 * FATURASIZ ÇIKIŞ — hedef: cepten 10 saniye (kabul kriteri 4).
 *
 * Bu yüzden form asgari tutuldu: cari + satır + tutar. Ürün kodu yazınca
 * faturasız hanedeki mevcut miktar ve liste fiyatı kendiliğinden gelir.
 * Kaydedince stok düşer VE alacak açılır — ikisi tek işlemde.
 */
export default function FaturasizCikis({ kapat, eklendi }: { kapat: () => void; eklendi: () => void }) {
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [urunler, setUrunler] = useState<Urun[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [customerAd, setCustomerAd] = useState('');
  const [customerTel, setCustomerTel] = useState('');
  const [satirlar, setSatirlar] = useState<Satir[]>([{ key: ++sayac, code: '', name: '', qty: '', unitTl: '' }]);
  const [totalTl, setTotalTl] = useState('');
  const [note, setNote] = useState('');
  const [dueNote, setDueNote] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  useEffect(() => {
    api<{ cariler: Cari[] }>('/api/cariler').then((r) => setCariler(r.cariler)).catch(() => undefined);
    api<{ satirlar: Urun[] }>('/api/urunler?durum=hepsi&limit=500')
      .then((r) => setUrunler(r.satirlar)).catch(() => undefined);
  }, []);

  function satirGuncelle(key: number, alan: Partial<Satir>) {
    setSatirlar((s) => s.map((x) => (x.key === key ? { ...x, ...alan } : x)));
  }

  /** Kod yazılınca ürünü bul, faturasız mevcudu ve fiyatı doldur. */
  function kodDegisti(key: number, kod: string) {
    const u = urunler.find((x) => x.code.toLowerCase() === kod.trim().toLowerCase());
    satirGuncelle(key, {
      code: kod.toUpperCase(),
      productId: u?.id,
      name: u?.name ?? '',
      mevcut: u?.offrec ?? undefined,
      unitTl: u?.listPriceKurus ? String(u.listPriceKurus / 100) : '',
    });
  }

  const satirToplam = satirlar.reduce(
    (t, s) => t + (Number(s.qty) || 0) * (Number(s.unitTl) || 0), 0
  );
  const hesapToplam = totalTl ? Number(totalTl) : satirToplam;

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBekliyor(true);
    try {
      await api('/api/faturasiz-cikis', {
        method: 'POST',
        body: JSON.stringify({
          customerId: customerId ? Number(customerId) : undefined,
          customerAd: customerId ? undefined : customerAd,
          customerTel: customerId ? undefined : customerTel,
          satirlar: satirlar
            .filter((s) => s.qty && (s.code || s.name))
            .map((s) => ({
              productId: s.productId, code: s.code || undefined, name: s.name || undefined,
              qty: Number(s.qty), unitTl: s.unitTl ? Number(s.unitTl) : undefined,
            })),
          totalTl: totalTl ? Number(totalTl) : undefined,
          note: note || undefined,
          dueNote: dueNote || undefined,
        }),
      });
      eklendi();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Faturasız çıkış">
        <form className="ccd-stack" onSubmit={gonder}>
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">Tahsilat</div>
              <h2 className="ccd-heading">Faturasız çıkış</h2>
            </div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>
              Kapat
            </button>
          </div>

          <div className="ccd-banner">
            <span className="ccd-banner-icon" aria-hidden="true">₺</span>
            <div className="ccd-meta">
              Kaydedince faturasız stok düşer <b>ve alacak kaydı açılır</b>. İkisi tek
              işlemde — biri olup diğeri olmaz.
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="cari">Cari</label>
            <AraSec id="cari" deger={customerId} degis={setCustomerId}
                    secenekler={cariler.map((c) => ({ id: c.id, ad: c.name }))}
                    yerTutucu="Cari adı yazın… (boş bırakırsanız yeni cari)" bosEtiket="— yeni cari —" />
          </div>

          {!customerId && (
            <div className="ccd-form-grid">
              <div className="ccd-field">
                <label className="ccd-field-label" htmlFor="cad">
                  Cari adı<span className="ccd-required"> *</span>
                </label>
                <input id="cad" className="ccd-input" required={!customerId}
                       value={customerAd} onChange={(e) => setCustomerAd(e.target.value)} />
              </div>
              <div className="ccd-field">
                <label className="ccd-field-label" htmlFor="ctel">Telefon</label>
                <input id="ctel" className="ccd-input" placeholder="+90 5xx…"
                       value={customerTel} onChange={(e) => setCustomerTel(e.target.value)} />
                <div className="ccd-field-hint">Girilirse WhatsApp hatırlatması açılır.</div>
              </div>
            </div>
          )}

          <div className="ccd-fieldset-title">Çıkan mal</div>
          {satirlar.map((s) => (
            <div key={s.key} className="ccd-card ccd-stack-sm">
              <div className="ccd-form-grid">
                <div className="ccd-field">
                  <label className="ccd-field-label">Ürün kodu</label>
                  <input className="ccd-input ccd-mono" list="urun-kodlari" placeholder="ARTBMRN7530G"
                         value={s.code} onChange={(e) => kodDegisti(s.key, e.target.value)} />
                  {s.name && <div className="ccd-field-hint">{s.name}</div>}
                  {s.mevcut != null && (
                    <div className="ccd-field-hint">
                      Faturasız hanede <b>{s.mevcut}</b> var
                    </div>
                  )}
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label">Miktar</label>
                  <input className="ccd-input" type="number" inputMode="decimal" step="any"
                         value={s.qty} onChange={(e) => satirGuncelle(s.key, { qty: e.target.value })} />
                </div>
              </div>
              <div className="ccd-form-grid">
                <div className="ccd-field">
                  <label className="ccd-field-label">Birim fiyat (TL)</label>
                  <input className="ccd-input" type="number" inputMode="decimal" step="any"
                         value={s.unitTl} onChange={(e) => satirGuncelle(s.key, { unitTl: e.target.value })} />
                </div>
                <div className="ccd-field" style={{ justifyContent: 'end' }}>
                  {satirlar.length > 1 && (
                    <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                            onClick={() => setSatirlar((x) => x.filter((y) => y.key !== s.key))}>
                      Satırı sil
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <datalist id="urun-kodlari">
            {urunler.map((u) => <option key={u.id} value={u.code}>{u.name}</option>)}
          </datalist>

          <button type="button" className="ccd-btn ccd-btn-sm"
                  onClick={() => setSatirlar((s) => [...s, { key: ++sayac, code: '', name: '', qty: '', unitTl: '' }])}>
            + Satır ekle
          </button>

          <div className="ccd-form-grid">
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="top">
                Toplam (TL)
              </label>
              <input id="top" className="ccd-input" type="number" inputMode="decimal" step="any"
                     placeholder={satirToplam ? String(satirToplam) : ''}
                     value={totalTl} onChange={(e) => setTotalTl(e.target.value)} />
              <div className="ccd-field-hint">
                Boş bırakılırsa satırlardan hesaplanır: {formatTl(Math.round(satirToplam * 100))}
              </div>
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="vade">Vade notu</label>
              <input id="vade" className="ccd-input" placeholder="2 hafta içinde"
                     value={dueNote} onChange={(e) => setDueNote(e.target.value)} />
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="not3">Not</label>
            <input id="not3" className="ccd-input" placeholder="Elden teslim, araçla gitti…"
                   value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
            </div>
          )}

          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>
            {bekliyor ? 'Kaydediliyor…'
              : `Çıkışı kaydet · ${formatTl(Math.round(hesapToplam * 100))} alacak aç`}
          </button>
        </form>
      </aside>
    </div>
  );
}
